// ProjectHeader.jsx — matches salesgro_crm_v8.html design system
// Fonts: Fraunces (display), Plus Jakarta Sans (body)
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

const T = {
  g800: '#1a4731', g700: '#1e5438', g50: '#eef7f1',
  mint: '#00c46d', mintSoft: 'rgba(0,196,109,.09)',
  ink: '#0f1f15', ink2: '#1e3a28', muted: '#506459',
  border: '#e0ece4', border2: '#c8ddd2',
  surface: '#f4f8f5', surface2: '#ebf4ee',
  red: '#dc2626', redBg: '#fef2f2', redText: '#991b1b',
  blue: '#2563eb', blueBg: '#eff6ff', blueText: '#1e40af',
  amber: '#f59e0b', amberBg: '#fef3c7', amberText: '#92400e',
  violet: '#7c3aed', violetBg: '#f5f3ff', violetText: '#5b21b6',
};

const BADGE_MAP = {
  'High Priority':   { bg: '#fef2f2', color: '#991b1b' },
  'High':            { bg: '#fef2f2', color: '#991b1b' },
  'Medium Priority': { bg: '#fef3c7', color: '#92400e' },
  'Medium':          { bg: '#fef3c7', color: '#92400e' },
  'Low Priority':    { bg: '#dcfce7', color: '#15803d' },
  'Low':             { bg: '#dcfce7', color: '#15803d' },
  Residential:       { bg: '#eff6ff', color: '#1e40af' },
  Commercial:        { bg: '#fef3c7', color: '#92400e' },
  Office:            { bg: '#f5f3ff', color: '#5b21b6' },
  Retail:            { bg: '#fef9c3', color: '#92400e' },
  Plot:              { bg: '#eef7f1', color: '#246040' },
  Villa:             { bg: '#ecfeff', color: '#164e63' },
  Luxury:            { bg: '#fef2f2', color: '#991b1b' },
};
const getBadge = (l) => BADGE_MAP[l] || { bg: T.surface2, color: T.muted };

// .badge — inline-flex with ::before dot
function Badge({ label }) {
  const s = getBadge(label);
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: '3px',
      px: '9px', py: '2px', borderRadius: '99px', lineHeight: 1.6,
      fontSize: '10.5px', fontWeight: 700, whiteSpace: 'nowrap',
      background: s.bg, color: s.color,
    }}>
      <Box component="span" sx={{
        width: '4px', height: '4px', borderRadius: '50%',
        background: 'currentColor', opacity: 0.6, flexShrink: 0,
        display: 'inline-block',
      }} />
      {label}
    </Box>
  );
}

export default function ProjectHeader({ project, onAddConfig }) {
  const navigate = useNavigate();
  if (!project) return null;

  const priority = project.priority;
  const tags = Array.isArray(project.tags)
    ? project.tags.filter((t) => t && !String(t).toLowerCase().includes('priority'))
    : [];
  const tagLabels = tags.length ? tags.slice(0, 4) : [project.category || 'Residential'].filter(Boolean);

  const subtitleParts = [
    project.developer,
    project.location || [project.city, project.state].filter(Boolean).join(', '),
    (project.rera || project.rera_no) ? `RERA: ${project.rera || project.rera_no}` : (project.rera_approved ? 'RERA Approved' : null),
    project.possession_date || project.possession || null,
  ].filter(Boolean);

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Name + badges */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', mb: '7px' }}>
          <Typography sx={{
            fontFamily: "'Fraunces', 'Georgia', serif",
            fontSize: '22px', fontWeight: 700,
            color: T.ink, letterSpacing: '-0.5px', lineHeight: 1.15,
          }}>
            {project.name || project.project_name}
          </Typography>
          {priority && <Badge label={`${priority} Priority`} />}
          {tagLabels.map((tag) => {
            const label = typeof tag === 'string' ? tag : (tag.label || tag);
            return <Badge key={label} label={label} />;
          })}
        </Box>
        {/* Subtitle .ph-sub */}
        <Typography sx={{ fontSize: '12px', color: T.muted, fontWeight: 400 }}>
          {subtitleParts.join(' · ')}
        </Typography>
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: '7px', alignItems: 'center', flexShrink: 0 }}>
        {/* .btn-secondary .btn-sm */}
        <Box component="button" onClick={() => navigate(`/inventory/projects/${project.id}/details`)}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            px: '11px', py: '5px', borderRadius: '8px',
            fontSize: '11.5px', fontWeight: 600, fontFamily: 'inherit',
            cursor: 'pointer', letterSpacing: '-0.1px',
            background: T.surface2, color: T.ink2,
            border: `1.5px solid ${T.border}`,
            transition: 'all 150ms ease',
            '&:hover': { borderColor: T.border2, background: T.surface },
          }}>
          ✏️ Edit
        </Box>
        {/* .btn-primary .btn-sm */}
        <Box component="button" onClick={onAddConfig}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            px: '11px', py: '5px', borderRadius: '8px',
            fontSize: '11.5px', fontWeight: 600, fontFamily: 'inherit',
            cursor: 'pointer', letterSpacing: '-0.1px',
            background: T.g800, color: '#fff', border: 'none',
            boxShadow: '0 1px 2px rgba(15,31,21,.06)',
            transition: 'all 150ms ease',
            '&:hover': { background: T.g700, boxShadow: '0 2px 8px rgba(15,31,21,.09)', transform: 'translateY(-1px)' },
          }}>
          + Add Config
        </Box>
      </Box>
    </Box>
  );
}
